import { useCallback } from 'react';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
import { Input } from '@amzn/stencil-react-components/input';
import { Button, ButtonSize, ButtonVariant } from '@amzn/stencil-react-components/button';
// standards-ignore: cross-feature — IconPlusSmall/IconBinSmall are generic affordances; a shared wrapper over a bare icon would add indirection without value
import { IconPlusSmall, IconBinSmall } from '@amzn/stencil-react-icons';

import { FieldErrorText } from './FieldErrorText';
import { countNonEmptyOptions, MIN_OPTIONS } from '../../domain/optionsValidation';

/** Visible copy — supplied by the caller so this stays i18n-bundle-agnostic
 *  and reusable across features. */
export interface OptionsEditorLabels {
  /** Placeholder + accessible name for each option's text input. The row
   *  number is appended for the accessible name, e.g. "Option 1". */
  readonly optionPlaceholder: string;
  readonly optionAriaLabel: string;
  readonly addButton: string;
  /** Accessible name for each row's icon-only delete button (row number
   *  appended). */
  readonly removeAltText: string;
  /** Inline hint shown when fewer than two non-empty options exist. */
  readonly minOptionsText: string;
}

export interface OptionsEditorProps {
  /** Current option strings, in render order. */
  readonly value: readonly string[];
  /** Emits the whole next list on add/remove/edit. */
  readonly onChange: (next: string[]) => void;
  readonly labels: OptionsEditorLabels;
  readonly disabled?: boolean;
  /** When true, the min-options hint renders as a critical (red) error with
   *  `role="alert"` instead of a passive secondary hint — the caller gates a
   *  publish/validation action on the same `countNonEmptyOptions < MIN_OPTIONS`
   *  measure. Defaults to the passive hint. */
  readonly hasError?: boolean;
  readonly dataTestId?: string;
}

/**
 * Repeatable single-value editor for a select-type's answer options
 * (SINGLE_SELECT / MULTI_SELECT). One text input per option with a trailing
 * delete, plus an Add control that appends a blank row. Fully controlled: the
 * owner holds the array and this emits the whole next list on every edit.
 *
 * Structural sibling of {@link DocLinksField} (same add/remove list shape and
 * test-id conventions), narrowed to a flat string list. Validation is
 * advisory here — it surfaces a min-options hint via {@link countNonEmptyOptions}
 * but never blocks typing; the owning form gates save on the same count.
 */
export function OptionsEditor({
  value,
  onChange,
  labels,
  disabled = false,
  hasError = false,
  dataTestId,
}: OptionsEditorProps): JSX.Element {
  const handleEdit = useCallback(
    (index: number, text: string) => {
      onChange(value.map((opt, i) => (i === index ? text : opt)));
    },
    [value, onChange],
  );

  const handleRemove = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
    },
    [value, onChange],
  );

  const handleAdd = useCallback(() => {
    onChange([...value, '']);
  }, [value, onChange]);

  const belowMin = countNonEmptyOptions(value) < MIN_OPTIONS;

  return (
    <Col gridGap="dimensions.spacing.200" width="100%">
      {value.map((option, index) => (
        <Row
          // eslint-disable-next-line @eslint-react/no-array-index-key -- rows never reorder and removal is index-based (handleRemove(index)); option strings can be blank or duplicate while editing, so a value key would collide
          key={index}
          alignItems="center"
          gridGap="dimensions.spacing.200"
          data-testid={dataTestId ? `${dataTestId}-row-${index}` : undefined}
        >
          <Col flex={1} minWidth={0}>
            <Input
              value={option}
              onChange={(e) => handleEdit(index, e.target.value)}
              placeholder={labels.optionPlaceholder}
              aria-label={`${labels.optionAriaLabel} ${index + 1}`}
              disabled={disabled}
              data-testid={dataTestId ? `${dataTestId}-input-${index}` : undefined}
            />
          </Col>
          <Button
            size={ButtonSize.Small}
            variant={ButtonVariant.Tertiary}
            icon={<IconBinSmall aria-hidden="true" />}
            aria-label={`${labels.removeAltText} ${index + 1}`}
            disabled={disabled}
            onClick={() => handleRemove(index)}
            data-testid={dataTestId ? `${dataTestId}-remove-${index}` : undefined}
          />
        </Row>
      ))}

      <Row>
        <Button
          size={ButtonSize.Small}
          variant={ButtonVariant.Secondary}
          icon={<IconPlusSmall aria-hidden="true" />}
          disabled={disabled}
          onClick={handleAdd}
          data-testid={dataTestId ? `${dataTestId}-add` : undefined}
        >
          {labels.addButton}
        </Button>
      </Row>

      {/* Min-options hint LAST — below the inputs and the Add control — so the
       *  field reads label → controls → error message, matching the shared
       *  FormField footer (not floating above the control when there are no
       *  options yet). */}
      {belowMin ? renderMinHint(hasError, labels.minOptionsText, dataTestId) : null}
    </Col>
  );
}

/**
 * The min-options hint. In the ERROR state it renders a critical-red icon +
 * message (`role="alert"`), matching the shared FormField footer so answer
 * options read like the other validated fields. Passive (non-error) below-min
 * state stays a plain secondary hint with no icon.
 */
function renderMinHint(
  hasError: boolean,
  text: string,
  dataTestId?: string,
): JSX.Element {
  const testId = dataTestId ? `${dataTestId}-min-hint` : undefined;
  if (!hasError) {
    return (
      <Text color="color.text.secondary" fontSize="T100" data-testid={testId}>
        {text}
      </Text>
    );
  }
  return (
    <FieldErrorText {...(testId !== undefined ? { dataTestId: testId } : {})}>
      {text}
    </FieldErrorText>
  );
}
